/**
 * PortalDocs — API & Integration Documentation
 *
 * Shows parameter documentation for advertisers and affiliates:
 * - Global postback URL + parameters
 * - Smart link format + parameters
 * - Embed script + parameters
 * - Available macros
 */

import React, { useState } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Copy, Check } from 'lucide-react';

type DocTab = 'advertiser' | 'affiliate' | 'api';

export default function PortalDocs() {
  const [tab, setTab] = useState<DocTab>('advertiser');
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-emerald-400" />
            Documentation
          </h1>
          <p className="text-sm text-slate-400 mt-1">Integration guides and parameter reference for advertisers and affiliates.</p>
        </div>

        <div className="flex gap-2">
          {([['advertiser', 'Advertiser Docs'], ['affiliate', 'Affiliate Docs'], ['api', 'API Reference']] as [DocTab, string][]).map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === k ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>{l}</button>
          ))}
        </div>

        {/* ═══ ADVERTISER DOCS ═══ */}
        {tab === 'advertiser' && (
          <div className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-base text-white">Global Postback URL</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-400">This is the URL you give to your advertisers. When a conversion happens on their end, they fire this URL to notify IntentDrive and trigger revenue tracking + affiliate payouts.</p>

                <CodeBlock label="Postback URL" code="https://intentdrive.ai/api/postback?session_id={session_id}&payout={payout}&status=converted&txn_id={txn_id}&offer_id={offer_id}&sub_id={sub_id}" onCopy={copy} copied={copied} />

                <h4 className="text-sm font-medium text-white mt-4">Parameters</h4>
                <ParamTable params={[
                  { name: 'session_id', required: true, desc: 'IntentDrive session ID. Passed to the advertiser in the original lead payload.' },
                  { name: 'payout', required: false, desc: 'Revenue amount (e.g., 45.00). Aliases: revenue, amount, sale_amount' },
                  { name: 'status', required: false, desc: 'Conversion status. Default: converted. Options: converted, rejected, returned, pending' },
                  { name: 'txn_id', required: false, desc: 'Advertiser transaction/reference ID. Aliases: transaction_id, txid, order_id' },
                  { name: 'offer_id', required: false, desc: 'IntentDrive offer ID (updates offer stats)' },
                  { name: 'sub_id', required: false, desc: 'Sub-ID for affiliate tracking. Aliases: subId, sub' },
                  { name: 'vertical', required: false, desc: 'Vertical slug (e.g., auto_insurance)' },
                  { name: 'buyer_id', required: false, desc: 'Advertiser identifier. Aliases: advertiser_id' },
                ]} />
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-base text-white">Everflow Integration</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-400">If the advertiser uses Everflow, use these Everflow macros in the postback URL:</p>
                <CodeBlock label="Everflow Postback" code="https://intentdrive.ai/api/postback?session_id={transaction_id}&payout={payout}&status=converted&txn_id={conversion_id}&offer_id={offer_id}&sub_id={sub1}" onCopy={copy} copied={copied} />
                <h4 className="text-sm font-medium text-white">Everflow Macro Mapping</h4>
                <ParamTable params={[
                  { name: '{transaction_id}', required: true, desc: 'Maps to our session_id — the click/session identifier' },
                  { name: '{payout}', required: true, desc: 'Conversion payout amount' },
                  { name: '{conversion_id}', required: false, desc: 'Everflow conversion ID → our txn_id' },
                  { name: '{offer_id}', required: false, desc: 'Everflow offer ID' },
                  { name: '{sub1}', required: false, desc: 'Sub-tracking parameter 1' },
                  { name: '{sub2}', required: false, desc: 'Sub-tracking parameter 2' },
                  { name: '{sub3}', required: false, desc: 'Sub-tracking parameter 3' },
                ]} />
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-base text-white">What Happens When Postback Fires</CardTitle></CardHeader>
              <CardContent>
                <ol className="text-sm text-slate-400 space-y-2 list-decimal pl-5">
                  <li>Conversion is logged in the <strong className="text-white">Outcomes</strong> table with the revenue amount</li>
                  <li>If an affiliate is linked to the session, their <strong className="text-white">earnings are credited</strong> automatically (rev share %)</li>
                  <li>Offer stats are updated (total leads, total revenue)</li>
                  <li>Any active <strong className="text-white">Global Postbacks</strong> are fired (your own S2S tracking)</li>
                  <li>Session value is updated for RPUS tracking</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ AFFILIATE DOCS ═══ */}
        {tab === 'affiliate' && (
          <div className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-base text-white">Embed Script</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-400">Affiliates add this script to their landing page. The IntentDrive widget appears as a floating button.</p>
                <CodeBlock label="Embed Code" code={`<script src="https://intentdrive.ai/embed.js"\n  data-vertical="auto_insurance"\n  data-affiliate-id="AFF-XXXX"\n  data-sub-id="my_campaign"\n  data-position="bottom-right"\n  data-color="#10b981"\n  data-text="Get Your Quote"\n  data-auto-open="15">\n</script>`} onCopy={copy} copied={copied} />

                <h4 className="text-sm font-medium text-white">Embed Parameters</h4>
                <ParamTable params={[
                  { name: 'data-vertical', required: true, desc: 'Primary vertical: auto_insurance, home_insurance, debt_relief, personal_loan, solar, medicare, mortgage_refi, etc.' },
                  { name: 'data-affiliate-id', required: true, desc: 'Affiliate tracking code (e.g., AFF-J4K9). Get from Affiliates page.' },
                  { name: 'data-sub-id', required: false, desc: 'Sub-ID for tracking specific campaigns, placements, or creatives.' },
                  { name: 'data-campaign-id', required: false, desc: 'Campaign identifier' },
                  { name: 'data-source', required: false, desc: 'Traffic source (e.g., facebook, google, email). Alias for affiliate-id.' },
                  { name: 'data-position', required: false, desc: 'Widget position: bottom-right (default) or bottom-left' },
                  { name: 'data-color', required: false, desc: 'Brand color in hex (default: #10b981)' },
                  { name: 'data-text', required: false, desc: 'CTA button text (default: Get Your Quote)' },
                  { name: 'data-auto-open', required: false, desc: 'Seconds before auto-opening (0 = manual only)' },
                  { name: 'data-brand-name', required: false, desc: 'Custom brand name shown in widget header' },
                  { name: 'data-brand-logo', required: false, desc: 'URL to a custom logo image' },
                  { name: 'data-brand-tagline', required: false, desc: 'Tagline shown under the brand name' },
                ]} />
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-base text-white">Smart Links</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-400">One URL that auto-detects the visitor's location and device, then routes to the highest-paying offer. No embed code needed.</p>
                <CodeBlock label="Smart Link Format" code="https://intentdrive.ai/go/{slug}?sub_id={sub_id}&click_id={click_id}&utm_source={source}" onCopy={copy} copied={copied} />

                <h4 className="text-sm font-medium text-white">Smart Link Parameters</h4>
                <ParamTable params={[
                  { name: 'slug', required: true, desc: 'Smart link slug (in the URL path, not query). Created in Smart Links page.' },
                  { name: 'sub_id', required: false, desc: 'Sub-ID for tracking. Aliases: sub' },
                  { name: 'click_id', required: false, desc: 'Click identifier for S2S tracking. Aliases: clk' },
                  { name: 'utm_source', required: false, desc: 'Traffic source (auto-captured from host page if using embed)' },
                  { name: 'utm_medium', required: false, desc: 'Traffic medium' },
                  { name: 'utm_campaign', required: false, desc: 'Campaign name' },
                ]} />
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-base text-white">UTM Parameter Passthrough</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-slate-400">The embed script automatically captures UTM parameters from the host page URL and passes them through to IntentDrive. No additional setup needed.</p>
                <div className="mt-3 text-xs text-slate-500">
                  <p>If the affiliate's page URL is:</p>
                  <code className="block bg-slate-900 p-2 rounded mt-1 text-emerald-400">https://affiliate-site.com/insurance?utm_source=facebook&utm_campaign=auto_q2&sub_id=ad_123</code>
                  <p className="mt-2">IntentDrive automatically captures: utm_source, utm_medium, utm_campaign, utm_content, utm_term, sub_id</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ API REFERENCE ═══ */}
        {tab === 'api' && (
          <div className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-base text-white">API Endpoints</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700">
                        <th className="text-left py-2 px-2">Endpoint</th>
                        <th className="text-center py-2 px-2">Method</th>
                        <th className="text-left py-2 px-2">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      {[
                        ['/api/postback', 'GET/POST', 'Advertiser conversion postback'],
                        ['/go/{slug}', 'GET', 'Smart link redirect'],
                        ['/try?vertical=X', 'GET', 'Direct widget page'],
                        ['/api/score-user', 'POST', 'Score a user and get routing decision'],
                        ['/api/track-event', 'POST', 'Ingest behavioral events'],
                        ['/api/log-outcome', 'POST', 'Log conversion outcome'],
                      ].map(([endpoint, method, desc]) => (
                        <tr key={endpoint as string} className="border-b border-slate-800">
                          <td className="py-2 px-2 font-mono text-xs text-emerald-400">{endpoint}</td>
                          <td className="py-2 px-2 text-center"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400">{method}</span></td>
                          <td className="py-2 px-2 text-xs">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-base text-white">Available Macros (for Postbacks & Smart Links)</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  {[
                    ['{session_id}', 'Unique session identifier'],
                    ['{click_id}', 'Click identifier (alias for session_id)'],
                    ['{affiliate_id}', 'Affiliate tracking code'],
                    ['{sub_id}', 'Sub-tracking parameter'],
                    ['{payout}', 'Conversion payout amount'],
                    ['{revenue}', 'Revenue amount (alias for payout)'],
                    ['{vertical}', 'Vertical slug'],
                    ['{offer_id}', 'Offer identifier'],
                    ['{buyer_id}', 'Advertiser identifier'],
                    ['{txn_id}', 'External transaction ID'],
                    ['{email}', 'Lead email (if captured)'],
                    ['{phone}', 'Lead phone (if captured)'],
                    ['{name}', 'Lead name (if captured)'],
                    ['{state}', 'US state'],
                    ['{decision}', 'Routing decision (CALL/LEAD/MULTI_OFFER)'],
                    ['{intent_score}', 'Intent score (0-100)'],
                    ['{monetization_score}', 'Monetization score (0-100)'],
                    ['{timestamp}', 'ISO 8601 timestamp'],
                  ].map(([macro, desc]) => (
                    <div key={macro as string} className="flex gap-2 py-1">
                      <code className="text-xs text-emerald-400 font-mono w-36 shrink-0">{macro}</code>
                      <span className="text-xs text-slate-500">{desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

function CodeBlock({ label, code, onCopy, copied }: { label: string; code: string; onCopy: (text: string, key: string) => void; copied: string | null }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-500">{label}</span>
        <button onClick={() => onCopy(code, label)} className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-white">
          {copied === label ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
          {copied === label ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="bg-slate-900 rounded-lg p-3 text-xs text-emerald-400 font-mono overflow-x-auto whitespace-pre-wrap break-all">{code}</pre>
    </div>
  );
}

function ParamTable({ params }: { params: Array<{ name: string; required: boolean; desc: string }> }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-slate-500 border-b border-slate-700">
            <th className="text-left py-1.5 px-2">Parameter</th>
            <th className="text-center py-1.5 px-2">Required</th>
            <th className="text-left py-1.5 px-2">Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map(p => (
            <tr key={p.name} className="border-b border-slate-800">
              <td className="py-1.5 px-2 font-mono text-xs text-emerald-400">{p.name}</td>
              <td className="py-1.5 px-2 text-center">{p.required ? <span className="text-red-400 text-xs">Yes</span> : <span className="text-slate-600 text-xs">No</span>}</td>
              <td className="py-1.5 px-2 text-xs text-slate-400">{p.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
